// 基础 getters
import calc from 'calculatorjs'
import {
  airVolumeG1,
  getSeShpMethod91VolumeLimitCm,
  volumeLimitSe,
} from '@g/utils/parcelUtils'

function computeCargoeAmt (form) {
  return form.jyoExtraInfo.declCargoes
    .filter((item) => item.customName)
    .reduce((pre, cur) => {
      return calc.add(pre, calc.mul(cur.count, cur.amount) || 0)
    }, 0)
}

export const getters = {
  shippingRatesInfo (state) {
    const form = state.createForm
    const prepare = state.createPrepare
    if (!prepare) {
      return {
        transport: '',
      }
    }
    return (
      prepare.shippingRates?.find((item) => form.srid === item.srid) || {
        transport: '',
      }
    )
  },
  scheduleShipment (state, getters, rootState) {
    const prepare = state.createPrepare
    const shippingRate = getters.shippingRatesInfo
    if (!prepare) return undefined
    let schedule
    if (shippingRate.transport === 'se') {
      schedule = shippingRate.srLabel === 'HZKZQ' || shippingRate.srLabel === 'HZKHD'
        ? prepare.scheduleShipmentHzq
        : prepare.scheduleShipmentSe
    } else {
      schedule = prepare.scheduleShipmentAir
    }
    if (String(rootState.project).endsWith('h5') && Array.isArray(schedule)) return {}
    return schedule
  },
  parcelAddrids (state) {
    if (!state.createPrepare?.parcels) return []
    return Array.from(new Set(state.createPrepare.parcels.map(item => item.jyAddrid)))
  },
  isBizFee (state, getters, rootState) {
    if (getters.isCompanyAddr) return true
    if (
      rootState.edition === 's' ||
      state.createForm?.useFeeType === 'busi' ||
      state.createForm?.useFeeType === 'notIncTax'
    ) {
      return state.createPrepare?.parcelsInfo?.jyParcelBiz > 0
    }
    return false
  },
  // brand-fence:start id=shared-store-company-tax-options brand=data-driven predicate=data-driven
  isBizeco (state, getters, rootState) {
    return getters.shippingRatesInfo.srLabel?.startsWith('BIZECO')
  },
  isAllIncTax (state, getters, rootState) {
    return getters.isBizeco || state.createPrepare.parcelsInfo.allItemInclTax === 'Y'
  },
  // 公司申报人 + MB-ECONOMY 资费：可选择不包税 / 全包税
  isCompanyMBOptions (state, getters) {
    if (!getters.isCompanyAddr) return false
    return getters.shippingRatesInfo.srLabel === 'MB-ECONOMY'
  },
  // 公司申报人 + MB-ECONOMY + 已选择包税（全包税）
  isCompanyMBPackedTax (state, getters) {
    if (!getters.isCompanyMBOptions) return false
    const useFeeType = state.createForm?.useFeeType
    return useFeeType === 'tax' || useFeeType === 'frt'
  },
  // brand-fence:end id=shared-store-company-tax-options
  hasWoodenParcels (state, getters, rootState) {
    return state.createPrepare.parcels.some(parcel => {
      return parcel.woodenSpt > 0 ||
      parcel.woodenBox > 0 ||
      parcel.woodenSptCov > 0 ||
      parcel.extraInfo.hasRawWood > 0
    })
  },
  // 后端汇总字段为手动登记商品的唯一判断依据，手动登记商品公司无法走 X2
  hasMockParcels (state) {
    return Number(state.createPrepare?.parcelsInfo?.hasPclItemMock) === 1
  },
  superLongParcels (state) {
    const prepare = state.createPrepare
    if (!prepare?.parcels) return []
    if (!prepare.parcels.find(parcel => !parcel.isSupserLong)) return [] // 都是超長件，就當成普通包裹處理
    return prepare.parcels.filter(parcel => parcel.isSupserLong)
  },
  largeParcels (state) {
    const prepare = state.createPrepare
    if (!prepare?.parcels) return []
    // 超過6才
    return prepare.parcels.filter(parcel => parcel.hctCai > 6)
  },
  x2VolumeLimit (state) {
    const prepare = state.createPrepare
    if (!prepare?.parcels) return false
    const volumeCai = prepare.parcels.reduce((prev, curr) => calc.add(prev, curr.hctCaiNoCeil), 0)
    return prepare.parcels.length > 1 && volumeCai >= 6
  },
  x2WeightLimit (state, getters) {
    const prepare = state.createPrepare
    if (!prepare?.parcels || prepare.parcels.length <= 1) return false

    if (getters.shippingRatesInfo.transport === 'se') {
      return prepare.parcelsInfo.jyWeight >= 40000
    }
    if (getters.shippingRatesInfo.transport === 'air') {
      return prepare.parcelsInfo.jyWeight >= 21000
    }
    return false
  },
  hasMutilTwParcel (state, getters) {
    const prepare = state.createPrepare
    if (!prepare?.parcels || prepare.parcels.length <= 1) return false

    return getters.hasWoodenParcels ||
      getters.largeParcels.length > 0 ||
      getters.superLongParcels.length > 0 ||
      getters.x2VolumeLimit ||
      getters.x2WeightLimit
  },
  onlyShpMethod91ByVolumeLimit (state, getters) {
    const prepare = state.createPrepare
    if (!prepare?.parcels) return false

    if (getters.shippingRatesInfo.transport === 'se') {
      const provider = prepare.lcmrouteSe?.twcc?.provider
      return prepare.parcels.some(parcel => {
        const maxWidth = getSeShpMethod91VolumeLimitCm({
          jyAddrid: parcel.jyAddrid || prepare.parcelsInfo?.jyAddrid,
          provider,
        })
        return volumeLimitSe(parcel, maxWidth)
      })
    }
    if (getters.shippingRatesInfo.transport === 'air') {
      return prepare.parcels.some(parcel => airVolumeG1(parcel))
    }
    return false
  },
  weightLight (state) {
    const prepare = state.createPrepare
    if (!prepare?.parcels || prepare.parcelsInfo?.jyWeight <= 0) return false
    return prepare.parcels.some(parcel => parcel.weight >= 69500)
  },
  c3Risk (state, getters, rootState) {
    const prepare = state.createPrepare
    const form = state.createForm
    const addrInfo = state.currAddrInfo
    const tradevanAddr = state.currTradevanAddr
    let baseRisk = 1
    if (addrInfo && tradevanAddr?.ccTypeC3Tot >= 2) {
      baseRisk = 3 // 高风险
    } else if (
      prepare.parcelsInfo.allItemInclTaxMaxWt !== '999999' ||
      !getters.isAllIncTax ||
      prepare.parcelsInfo.jyParcelBiz === 1 ||
      prepare.parcelsInfo.hasMultiSubtnos === 1
    ) {
      baseRisk = 2 // 中风险
    }
    // 公司簡易報關，貨值偏低時補升風險：< 1000 → 高，< 2000 → 中
    if (getters.isCompanyAddr && form?.jyoShpMethod !== '91') {
      const cargoeAmt = computeCargoeAmt(form)
      if (cargoeAmt < 1000) return Math.max(baseRisk, 3)
      if (cargoeAmt < 2000) return Math.max(baseRisk, 2)
    }
    return baseRisk
  },
  isLowCargoC3HighRisk (state, getters) {
    const form = state.createForm
    if (!getters.isCompanyAddr || form?.jyoShpMethod === '81') return false
    return computeCargoeAmt(form) < 1000
  },
  estimateTotalAmount (state) {
    return state.createPrepare.parcelsInfo.estimatedTaxAmount
  },
  estimateTax (state) {
    return state.createPrepare.parcelsInfo.estimatedTaxFees
  },
  estimateTaxRate (state, getters) {
    const amount = getters.estimateTotalAmount
    if (!amount) return 0
    const rate = calc.round(calc(`${getters.estimateTax} / ${amount} * 100`), 1)
    if (rate > 0 && rate < 5) {
      return 5
    }
    return rate
  },
  traRealAmtsTWDTotal (state, getters, rootState) {
    const prepare = state.createPrepare
    const traRealAmtsTWDTotal = prepare.parcelsInfo.jyCargoes.reduce((pre, cur) => calc.add(pre, cur.traRealAmtsTWD), 0)
    return traRealAmtsTWDTotal
  },
  traRealAmtsRMBTotal (state, getters, rootState) {
    const prepare = state.createPrepare
    return prepare.parcels.reduce((pre, cur) => {
      if (cur.extraInfo.tpttAmount > 0) {
        return calc.add(pre, cur.extraInfo.tpttAmount)
      } else {
        return calc.add(pre, 50)
      }
    }, 0)
  },
  // brand-fence:start id=shared-store-hzk-cb-rates brand=data-driven predicate=data-driven
  HZKZQRateList (state, getters, rootState) {
    const prepare = state.createPrepare
    const xiamenPickupOverweight = prepare.parcelsInfo.jyAddrid === '1592' &&
      prepare.parcels.some(parcel => parcel.weight > 30000)
    const list = (() => {
      if (!prepare.canHZKZQ) {
        return []
      }
      if (rootState.userData.alibabaOpenUserId) {
        const list = prepare.shippingRates.filter((item) => item.srLabel === 'CB1688' && item.srTwAddrid > 0)
        if (list.length) {
          return list
        }
      }
      if (prepare.parcelsInfo.jyAddrid === '1592') {
        return prepare.shippingRates.filter((item) => item.srLabel === 'HZKZQ')
      }
      return (
        prepare.shippingRates.filter((item) => item.srLabel === 'HZKZQ') || []
      )
    })()
    return list.map(srItem => {
      let disabledText = ''
      if (String(srItem.srTwAddrid) === '3807') {
        if (window.vueApp.$store.state.isTest) {
          disabledText = '商業件無法從高雄清關，因此無法使用自取'
        }
        if (prepare.parcelsInfo?.jyParcelBiz > 0 || prepare.parcelsInfo?.jyParcelBizTakao > 0) {
          disabledText = '商業件無法從高雄清關，因此無法使用自取'
        }
      }
      const storeDisabledText = xiamenPickupOverweight && ['3406', '3807'].includes(String(srItem.srTwAddrid))
        ? '自取店單個包裹重量限制30KG'
        : disabledText
      return {
        ...srItem,
        disabledText,
        storeDisabledText,
      }
    })
  },
  // brand-fence:end id=shared-store-hzk-cb-rates
  jyAddrid (state) {
    return state.createPrepare.parcels[0].jyAddrid
  },
  isX3OverTenConfirm (state) {
    return state.createForm.jyoShpMethod === '81' && state.createPrepare.parcels.filter(parcel => parcel.trackingNoPri).length > 10
  },
  hasPackFood (state) {
    return state.createPrepare?.parcels?.find(parcel => parcel.extraInfo.tpTaobaoTradeItems.find(tpItem => tpItem.itemMold === '2'))
  },
  hasFurniture (state) {
    return state.createPrepare?.parcelsInfo?.hasPclItemMold4 > 0
  },
  freeDgParcelInfo (state, getters, rootState) {
    const prepare = state.createPrepare
    const parcels =
      prepare.parcels?.filter(
        (parcel) =>
          parcel.extraInfo.srcPfmShippingFree > 0
      ) || []
    const weight = parcels.reduce((pre, cur) => {
      return calc.add(pre, cur.weight)
    }, 0)

    const shippingRatesInfo = getters.shippingRatesInfo
    let fee = 0
    if (shippingRatesInfo.fwFee > 0) {
      fee = calc.round(calc.mul(weight / 1000, shippingRatesInfo.fwFee), 0)
    }
    return {
      weight,
      fee,
    }
  },
  prePackCount (state, getters) {
    const prepare = state.createPrepare
    if (!prepare?.parcels) return 0
    const lgCount = prepare.parcels.filter(item => item.hctCaiNoCeil >= 4).length
    // 其他有体积的，就每4才算一个包裹
    const mdCount = Math.ceil(prepare.parcels.filter(item => item.hctCaiNoCeil >= 0 && item.hctCaiNoCeil < 4)
      .reduce((acc, item) => acc + item.hctCaiNoCeil, 0) / 4)
    const smCount = prepare.parcels.filter(item => item.hctCaiNoCeil === 0).length
    return lgCount + Math.min(smCount, 1) + mdCount
  },
}
